import React, { useMemo, useState } from 'react';
import { Button, FormField, MobileHeader, MobileCard, mobileInputCls } from '../ui';
import PasswordInput from '../ui/PasswordInput';
import { supabase } from '../../lib/supabase';
import { clearPersistedToken } from './portalShared';

// ---------------------------------------------------------------------------
// Crew-portal auth screens (crew-auth-plan-v1: email + password only). Same
// charcoal header as the signed-in shell (mobile.md), one card, one big
// submit. The Supabase session persists in localStorage, so in practice
// crew sign in once per device.
// ---------------------------------------------------------------------------

export function AuthShell({ children, subtitle }) {
    return (
        <div className="theme-fixed fixed inset-0 flex flex-col bg-slate-100 text-slate-900">
            <MobileHeader title="Crew portal" sub={subtitle} />
            <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-3">
                <div className="w-full max-w-sm mx-auto space-y-3">{children}</div>
            </div>
        </div>
    );
}

export function CrewSignInScreen({ onAuthed }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const submit = async (e) => {
        if (e) e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            const { error: err } = await supabase.auth.signInWithPassword({
                email: email.trim().toLowerCase(),
                password,
            });
            if (err) {
                setError(err.message === 'Invalid login credentials' ? 'Wrong email or password.' : err.message);
                return;
            }
            onAuthed();
        } catch (err) {
            setError(err?.message || 'Network error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AuthShell subtitle="Sign in">
            <MobileCard>
                <form onSubmit={submit}>
                    <h2 className="font-bold text-slate-900 text-[16px]">Sign in to your portal</h2>
                    <p className="text-xs text-slate-500 mb-4">You'll stay signed in on this device.</p>
                    <FormField label="Email" className="mb-3">
                        <input
                            type="email"
                            inputMode="email"
                            autoComplete="username"
                            autoCapitalize="off"
                            className={mobileInputCls}
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            placeholder="you@example.com"
                        />
                    </FormField>
                    <FormField label="Password" className="mb-3" error={error}>
                        <PasswordInput
                            autoComplete="current-password"
                            className={mobileInputCls}
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            placeholder="••••••••"
                        />
                    </FormField>
                    <Button type="submit" size="lg" full loading={submitting} disabled={!email.trim() || !password} className="mt-2">
                        Sign in
                    </Button>
                </form>
            </MobileCard>
            <p className="text-xs text-slate-400 text-center px-2">
                Forgot your password? Ask the office to re-send your portal invite — the new setup link lets you pick a fresh one.
            </p>
        </AuthShell>
    );
}

// /crew/setup?token=… — landing page for the SMS invite link
// (api/crew-invite.js). Sets the real password via
// api/crew-complete-setup.js, then signs straight in so the crew member
// lands in the portal without re-typing anything.
export function CrewSetupScreen() {
    const setupToken = useMemo(() => {
        try { return new URLSearchParams(window.location.search).get('token') || ''; }
        catch { return ''; }
    }, []);
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const submit = async (e) => {
        if (e) e.preventDefault();
        if (submitting) return;
        if (password.length < 8) { setError('At least 8 characters'); return; }
        if (password !== confirm) { setError("Passwords don't match"); return; }
        setSubmitting(true);
        setError(null);
        try {
            const res = await fetch('/api/crew-complete-setup', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: setupToken, new_password: password }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(body.error || `Setup failed (${res.status})`);
                return;
            }
            // Sign in immediately with the fresh credentials, then land in
            // the portal. If the sign-in hiccups, the portal's sign-in
            // screen catches them — the password IS set at this point.
            clearPersistedToken();
            await supabase.auth.signInWithPassword({ email: body.email, password }).catch(() => {});
            window.location.replace('/crew');
        } catch (err) {
            setError(err?.message || 'Network error');
        } finally {
            setSubmitting(false);
        }
    };

    if (!setupToken) {
        return (
            <AuthShell subtitle="Setup">
                <MobileCard className="text-center py-6">
                    <h2 className="font-bold text-slate-900 mb-1">Missing setup link</h2>
                    <p className="text-sm text-slate-500">Open the setup link from your text message, or ask the office to send a new one.</p>
                </MobileCard>
            </AuthShell>
        );
    }

    return (
        <AuthShell subtitle="Set up your login">
            <MobileCard>
                <form onSubmit={submit}>
                    <h2 className="font-bold text-slate-900 text-[16px]">Create your password</h2>
                    <p className="text-xs text-slate-500 mb-4">You'll use this to sign in to the crew portal. Your browser can save it for you.</p>
                    <FormField label="New password" className="mb-3">
                        <PasswordInput
                            autoComplete="new-password"
                            className={mobileInputCls}
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            placeholder="At least 8 characters"
                        />
                    </FormField>
                    <FormField label="Confirm password" className="mb-3" error={error}>
                        <PasswordInput
                            autoComplete="new-password"
                            className={mobileInputCls}
                            value={confirm}
                            onChange={e => setConfirm(e.target.value)}
                            placeholder="Same again"
                        />
                    </FormField>
                    <Button type="submit" size="lg" full loading={submitting} disabled={!password || !confirm} className="mt-2">
                        Set password &amp; sign in
                    </Button>
                </form>
            </MobileCard>
        </AuthShell>
    );
}
